import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Navigate } from "react-router-dom";
import axiosFetch from "../../utils/axiosFetch";

const STEP_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  SUCCESS: "success",
  FAILED: "failed",
};

const formatClientError = (value) => {
  if (value instanceof Error) {
    return value.stack || value.message;
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const StepStatus = ({ status }) => {
  if (status === STEP_STATUS.RUNNING) {
    return <Chip size="small" icon={<CircularProgress size={14} />} label="In progress" />;
  }

  if (status === STEP_STATUS.SUCCESS) {
    return <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Success" />;
  }

  if (status === STEP_STATUS.FAILED) {
    return <Chip size="small" color="error" icon={<ErrorIcon />} label="Failed" />;
  }

  return <Chip size="small" variant="outlined" label="Not started" />;
};

const StepPanel = ({ title, status, message, children }) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <Typography variant="h6">{title}</Typography>
      <StepStatus status={status} />
    </Box>
    {message && (
      <Alert severity={status === STEP_STATUS.FAILED ? "error" : "info"} sx={{ mb: 1 }}>
        {message}
      </Alert>
    )}
    {children}
  </Paper>
);

export const SalesforceAdmin = () => {
  const captureClientErrors = useRef(false);
  const scriptLoaded = useRef(false);
  // Lightning Out's registry is global to the page; once a component is registered
  // under one app-id, registering it again under a different app-id throws. Once the
  // Salesforce component has mounted, force a full reload instead of re-rendering it.
  const salesforceComponentMounted = useRef(false);

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [licenses, setLicenses] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [licenseId, setLicenseId] = useState("");
  const [userType, setUserType] = useState("");
  const [optionsError, setOptionsError] = useState(null);

  const [tokenStatus, setTokenStatus] = useState(STEP_STATUS.IDLE);
  const [tokenMessage, setTokenMessage] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [showToken, setShowToken] = useState(false);

  const [frontdoorStatus, setFrontdoorStatus] = useState(STEP_STATUS.IDLE);
  const [frontdoorMessage, setFrontdoorMessage] = useState(null);
  const [frontdoorUrl, setFrontdoorUrl] = useState(null);
  const [appId, setAppId] = useState(null);
  const [lightningOutUrl, setLightningOutUrl] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const [componentLocked, setComponentLocked] = useState(false);

  useEffect(() => {
    if (frontdoorStatus !== STEP_STATUS.SUCCESS || !lightningOutUrl || scriptLoaded.current) {
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = lightningOutUrl;
    script.onerror = () => setFrontdoorMessage("Failed to load the Salesforce Lightning Out script");
    document.head.appendChild(script);
    scriptLoaded.current = true;
    salesforceComponentMounted.current = true;
    setComponentLocked(true);
  }, [frontdoorStatus, lightningOutUrl]);

  useEffect(() => {
    const originalConsoleError = console.error;
    const addClientError = (...values) => {
      if (!captureClientErrors.current) {
        return;
      }

      const message = values.map(formatClientError).join(" ");
      setClientErrors((currentErrors) =>
        currentErrors.includes(message) ? currentErrors : [...currentErrors, message]
      );
    };
    const handleWindowError = (event) => addClientError(event.error || event.message);
    const handleUnhandledRejection = (event) => addClientError(event.reason);

    console.error = (...values) => {
      originalConsoleError(...values);
      addClientError(...values);
    };
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      console.error = originalConsoleError;
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosFetch.get("/auth/sso/salesforce/admin/access");
        setIsAdmin(Boolean(data?.isAdmin));
      } catch {
        setIsAdmin(false);
      } finally {
        setCheckingAccess(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    (async () => {
      try {
        const [licenseRes, userTypeRes] = await Promise.all([
          axiosFetch.get("/auth/sso/salesforce/admin/licenses"),
          axiosFetch.get("/auth/sso/salesforce/admin/user-types"),
        ]);

        setLicenses(licenseRes.data?.licenses ?? []);
        setUserTypes(userTypeRes.data?.userTypes ?? []);
        setOptionsError(null);
      } catch (err) {
        setOptionsError(
          err.response?.data?.message ?? "Failed to load licenses and user types"
        );
      }
    })();
  }, [isAdmin]);

  const generateFrontdoor = useCallback(async () => {
    setFrontdoorStatus(STEP_STATUS.RUNNING);
    setFrontdoorMessage(null);

    try {
      const { data } = await axiosFetch.post("/auth/sso/salesforce/admin/frontdoor");
      setFrontdoorUrl(data?.frontdoorUrl ?? null);
      setAppId(data?.appId ?? null);
      setLightningOutUrl(data?.lightningOutUrl ?? null);
      captureClientErrors.current = true;
      setFrontdoorStatus(STEP_STATUS.SUCCESS);
    } catch (err) {
      setFrontdoorStatus(STEP_STATUS.FAILED);
      setFrontdoorMessage(
        err.response?.data?.message ?? "Failed to generate the Salesforce frontdoor URL"
      );
    }
  }, []);

  const handleGenerate = async () => {
    if (salesforceComponentMounted.current) {
      // A fresh registry is the only reliable way to avoid a duplicate registration error.
      window.location.reload();
      return;
    }

    setTokenStatus(STEP_STATUS.RUNNING);
    setTokenMessage(null);
    setAccessToken(null);
    setShowToken(false);
    setFrontdoorStatus(STEP_STATUS.IDLE);
    setFrontdoorMessage(null);
    setFrontdoorUrl(null);
    setClientErrors([]);
    captureClientErrors.current = false;

    try {
      const { data } = await axiosFetch.post("/auth/sso/salesforce/admin/token", {
        licenseId,
        userType,
      });
      setAccessToken(data?.accessToken ?? null);
      setTokenStatus(STEP_STATUS.SUCCESS);
      await generateFrontdoor();
    } catch (err) {
      setTokenStatus(STEP_STATUS.FAILED);
      setTokenMessage(
        err.response?.data?.message ?? "Failed to generate the Salesforce user token"
      );
    }
  };

  if (checkingAccess) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/unauthorized" />;
  }

  const isReady = licenseId && userType && tokenStatus !== STEP_STATUS.RUNNING;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Salesforce Admin
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        {optionsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {optionsError}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel id="salesforce-license-label">License</InputLabel>
              <Select
                labelId="salesforce-license-label"
                label="License"
                value={licenseId}
                onChange={(event) => setLicenseId(event.target.value)}
              >
                {licenses.map(({ id, license }) => (
                  <MenuItem key={id} value={id}>
                    {license ? `${id} (${license})` : id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="salesforce-usertype-label">User Type</InputLabel>
              <Select
                labelId="salesforce-usertype-label"
                label="User Type"
                value={userType}
                onChange={(event) => setUserType(event.target.value)}
              >
                {userTypes.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button fullWidth variant="contained" disabled={!isReady} onClick={handleGenerate}>
              {componentLocked ? "Reload to generate" : "Generate"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <StepPanel title="1. Salesforce user token" status={tokenStatus} message={tokenMessage}>
        {accessToken && (
          <Box>
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-all", fontFamily: "monospace", mb: 1 }}
            >
              {showToken ? accessToken : `${accessToken.slice(0, 12)}${"•".repeat(24)}`}
            </Typography>
            <Button size="small" onClick={() => setShowToken((shown) => !shown)}>
              {showToken ? "Hide token" : "Show token"}
            </Button>
          </Box>
        )}
      </StepPanel>

      <StepPanel
        title="2. Frontdoor URL"
        status={frontdoorStatus}
        message={frontdoorMessage}
      >
        {frontdoorUrl && (
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {frontdoorUrl}
          </Typography>
        )}
        {frontdoorStatus === STEP_STATUS.FAILED && tokenStatus === STEP_STATUS.SUCCESS && (
          <Button size="small" sx={{ mt: 1 }} onClick={generateFrontdoor}>
            Retry
          </Button>
        )}
      </StepPanel>

      {tokenStatus === STEP_STATUS.SUCCESS && frontdoorStatus === STEP_STATUS.SUCCESS && (
        <Paper sx={{ p: 2, minHeight: "1000px" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            3. Salesforce component
          </Typography>

          {clientErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Client errors</Typography>
              {clientErrors.map((message) => (
                <Typography
                  key={message}
                  component="pre"
                  variant="body2"
                  sx={{ m: 0, mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message}
                </Typography>
              ))}
            </Alert>
          )}

          <lightning-out-application
            app-id={appId}
            frontdoor-url={frontdoorUrl}
            components="c-peru-web-to-case"
          />

          <c-peru-web-to-case style={{ height: "1000px" }} />
        </Paper>
      )}
    </Box>
  );
};

export default SalesforceAdmin;
